import { useForm } from "react-hook-form";
import { Offcanvas, Form, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import API_BASE_URL from "../../../config/api";
import { Box, Divider, Typography } from "@mui/material";
import {
  PersonPlusFill,
  ShieldLockFill,
  GeoAltFill,
  ImageFill,
  TelephoneFill,
  EnvelopeFill,
  KeyFill,
  CalendarDateFill,
  BriefcaseFill,
} from "react-bootstrap-icons";

// Reusable SectionHeader component
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddMentorOffcanvas = ({ show, handleClose, onMentorAdded }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/location/admin/getAllLocations`,
        );

        // Filter only active locations
        const activeLocations = (response.data?.data || [])
          .filter((location) => location.status === "Active")
          .map((location) => ({
            _id: location._id,
            location: location.location,
            status: location.status,
          }));

        setLocations(activeLocations);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Failed to load locations");
      }
    };

    fetchLocations();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("number", data.number);
    formData.append("email", data.email);
    formData.append("password", data.password);
    formData.append("confirmPassword", data.confirmPassword);
    formData.append("dateofbirth", formatDate(data.dateofbirth));
    formData.append("location", data.location);
    formData.append("expertise", data.expertise);
    formData.append("files", data.files[0]);

    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/adminRegister/mentor`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Mentor registered successfully!");
      onMentorAdded();
      reset();
      handleClose();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 480 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonPlusFill size={13} color="#ffeb3b" />
          </Box>
          Add New Mentor
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="p-3 rounded-3 shadow-sm bg-white"
        >
          <SectionHeader
            icon={<PersonPlusFill size={13} />}
            label="Personal Information"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Full Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter full name"
              {...register("name", { required: "Name is required" })}
              style={fieldStyle}
              isInvalid={!!errors.name}
            />
            {errors.name && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.name.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Phone Number <span className="text-danger">*</span>
            </Form.Label>
            <PhoneInput
              country={"no"}
              enableSearch={true}
              inputStyle={{
                ...fieldStyle,
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                border: "1.5px solid #dde4f5",
                borderRadius: "8px 0 0 8px",
              }}
              specialLabel=""
              value={watch("number")}
              onChange={(phone) => {
                setValue("number", phone, { shouldValidate: true });
                if (errors.number) clearErrors("number");
              }}
            />
            {errors.number && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.number.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Email Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email address"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              style={fieldStyle}
              isInvalid={!!errors.email}
            />
            {errors.email && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.email.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader
            icon={<ShieldLockFill size={13} />}
            label="Security Credentials"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password (min. 6 characters)"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
              style={fieldStyle}
              isInvalid={!!errors.password}
            />
            {errors.password && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.password.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Confirm Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="password"
              placeholder="Re-enter password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (val) =>
                  val === watch("password") || "Passwords do not match",
              })}
              style={fieldStyle}
              isInvalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.confirmPassword.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader
            icon={<CalendarDateFill size={13} />}
            label="Additional Details"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Date of Birth</Form.Label>
            <Form.Control
              type="date"
              {...register("dateofbirth", {
                validate: {
                  notFuture: (value) => {
                    if (!value) return true;
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return (
                      selectedDate <= today || "Date cannot be in the future"
                    );
                  },
                },
              })}
              style={fieldStyle}
              isInvalid={!!errors.dateofbirth}
              max={new Date().toISOString().split("T")[0]}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            />
            {errors.dateofbirth && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.dateofbirth.message}
              </div>
            )}
            <Form.Text muted>Stored as DD-MM-YYYY format</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              {...register("location", { required: "Location is required" })}
              style={fieldStyle}
              isInvalid={!!errors.location}
            >
              <option value="">Select location</option>
              {locations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
            {errors.location && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.location.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Expertise <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Data Science, Web Development"
              {...register("expertise", { required: "Expertise is required" })}
              style={fieldStyle}
              isInvalid={!!errors.expertise}
            />
            {errors.expertise && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.expertise.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader icon={<ImageFill size={13} />} label="Profile Image" />

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>Upload Profile Picture</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              {...register("files")}
              style={fieldStyle}
            />
            <Form.Text muted>
              Optional: Upload a profile image (JPG, PNG)
            </Form.Text>
            {errors.files && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.files.message}
              </div>
            )}
          </Form.Group>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
            style={{
              background: "linear-gradient(135deg, #1a237e, #0d47a1)",
              border: "none",
              borderRadius: "10px",
              padding: "11px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" /> Registering Mentor...
              </>
            ) : (
              <>
                <PersonPlusFill size={15} /> Register Mentor
              </>
            )}
          </Button>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddMentorOffcanvas;
